// pages/Login.jsx
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

import {
  Box, Stack, Typography, TextField, Button,
  IconButton, InputAdornment, Alert, CircularProgress,
  GlobalStyles, Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { saveUserData } from './services/userService';

/* ─────────────────────────────────────────────────────────────────
   TOKENS  (single source of truth for the warm-luxury palette)
───────────────────────────────────────────────────────────────── */
const T = {
  cream:          '#faf7f2',
  sand:           '#e8ddd0',
  clay:           '#ede5d8',
  terracotta:     '#c17f59',
  terracottaDark: '#9a6040',
  brown:          '#5a3e2b',
  brownDeep:      '#2a1a0e',
  muted:          '#8a6e5a',
  placeholder:    '#c9b9a8',
  serif:          "'Cormorant Garamond', serif",
  sans:           "'Outfit', sans-serif",
};

/* ─────────────────────────────────────────────────────────────────
   GLOBAL — sólo keyframes, sin @import
   Las fuentes se cargan desde public/index.html con:
     <link rel="preconnect" href="https://fonts.googleapis.com">
     <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
     <link rel="stylesheet"
           href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Outfit:wght@300;400;500&display=swap"
           media="print" onload="this.media='all'">
     <noscript>
       <link rel="stylesheet" href="https://fonts.googleapis.com/css2?...">
     </noscript>
───────────────────────────────────────────────────────────────── */
const globalStyles = (
  <GlobalStyles styles={`
    @keyframes cardIn  { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
    @keyframes shake   { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-4px)} 75%{transform:translateX(4px)} }
    @keyframes spin    { to{transform:rotate(360deg)} }
    @keyframes shimmer { to{background-position:right} }
  `} />
);

/* ─────────────────────────────────────────────────────────────────
   STYLED COMPONENTS
───────────────────────────────────────────────────────────────── */
const WarmField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    fontFamily: T.sans,
    fontSize: 14,
    color: T.brownDeep,
    backgroundColor: '#fff',
    borderRadius: 12,
    transition: 'background .2s',
    '& fieldset': { borderColor: T.sand, borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: `${T.terracotta}88` },
    '&.Mui-focused': {
      backgroundColor: '#fffcf9',
      '& fieldset': { borderColor: T.terracotta, borderWidth: 1.5 },
    },
    '&.Mui-disabled': { backgroundColor: '#f7f3ef', opacity: .6 },
  },
  '& .MuiInputAdornment-root svg': { color: '#c4a48a', fontSize: 18 },
  '& .Mui-focused .MuiInputAdornment-root svg': { color: T.terracotta },
  '& input::placeholder': { color: T.placeholder, opacity: 1 },
  '& .MuiFormHelperText-root': { fontFamily: T.sans, fontSize: 12, marginLeft: 2 },
  '& .MuiInputLabel-root': {
    fontFamily: T.sans, fontSize: 11, fontWeight: 500,
    letterSpacing: '1px', textTransform: 'uppercase', color: '#a07858',
    transform: 'none', top: -24, left: 0,
    '&.Mui-focused': { color: T.terracotta },
    '&.MuiInputLabel-shrink': { transform: 'none' },
  },
});

const PrimaryBtn = styled(Button)({
  fontFamily: T.sans,
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: '.4px',
  borderRadius: 12,
  padding: '15px 24px',
  minHeight: 52,
  background: `linear-gradient(135deg, #b87348 0%, #d4956a 50%, #b87348 100%)`,
  backgroundSize: '200%',
  boxShadow: '0 4px 18px rgba(193,127,89,.32)',
  color: '#fff',
  textTransform: 'none',
  transition: 'transform .2s, box-shadow .2s, background-position .4s',
  overflow: 'hidden',
  '&::after': {
    content: '""', position: 'absolute', inset: 0,
    background: 'linear-gradient(105deg,transparent 30%,rgba(255,255,255,.3) 50%,transparent 70%)',
    transform: 'translateX(-100%)', transition: 'transform .55s',
  },
  '&:hover:not(:disabled)': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 26px rgba(193,127,89,.42)',
    backgroundPosition: 'right',
    '&::after': { transform: 'translateX(100%)' },
  },
  '&:active:not(:disabled)': { transform: 'translateY(0)' },
  '&:disabled': { opacity: .45, color: '#fff', backgroundImage: 'none', backgroundColor: '#c17f59' },
});

const OutlineBtn = styled(Button)({
  fontFamily: T.sans,
  fontSize: 14,
  fontWeight: 400,
  borderRadius: 12,
  padding: '13px 24px',
  border: `1.5px solid ${T.sand}`,
  color: T.brown,
  backgroundColor: '#fff',
  textTransform: 'none',
  boxShadow: '0 1px 4px rgba(0,0,0,.06)',
  transition: 'border-color .2s, box-shadow .2s, transform .2s, background .2s',
  '&:hover:not(:disabled)': {
    borderColor: T.terracotta,
    boxShadow: '0 4px 14px rgba(193,127,89,.14)',
    transform: 'translateY(-1px)',
    backgroundColor: '#fffcf9',
  },
  '&:disabled': { opacity: .45 },
});

/* ─────────────────────────────────────────────────────────────────
   SVG ASSETS
───────────────────────────────────────────────────────────────── */
const GoogleSVG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.1 0 5.9 1.1 8.1 2.9l6-6C34.4 3.1 29.5 1 24 1 14.8 1 7 6.7 3.7 14.6l7 5.4C12.5 13.3 17.8 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.4c-.5 2.8-2.1 5.1-4.4 6.7l7 5.4C43.2 37 46.1 31.2 46.1 24.5z"/>
    <path fill="#FBBC05" d="M10.7 28.6C10.2 27.2 10 25.6 10 24s.2-3.2.7-4.6l-7-5.4C1.9 17 1 20.4 1 24s.9 7 2.7 10l7-5.4z"/>
    <path fill="#34A853" d="M24 46c5.5 0 10.1-1.8 13.5-4.9l-7-5.4c-1.8 1.2-4 2-6.5 2-6.2 0-11.5-3.8-13.3-9.1l-7 5.4C7 41.3 14.8 46 24 46z"/>
  </svg>
);

const LayersSVG = () => (
  <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, stroke: '#fff', fill: 'none', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
    <polyline points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);

/* ─────────────────────────────────────────────────────────────────
   DECORATIVE LEFT PANEL
───────────────────────────────────────────────────────────────── */
const DecorativePanel = ({ brand }) => (
  <Box sx={{
    display: { xs: 'none', md: 'block' },
    width: '42%', flexShrink: 0, position: 'relative', overflow: 'hidden',
    background: `radial-gradient(ellipse 90% 70% at 30% 80%, rgba(193,127,89,.25) 0%, transparent 55%),
                 radial-gradient(ellipse 70% 60% at 80% 20%, rgba(160,190,150,.22) 0%, transparent 55%),
                 #ede5d8`,
  }}>
    {[
      { size: 320, top: -80, left: -80, dur: '40s' },
      { size: 200, top:  40, left:  60, dur: '28s', reverse: true },
      { size: 440, bottom: 60, right: -160, dur: '55s' },
    ].map(({ size, dur, reverse, ...pos }, i) => (
      <Box key={i} sx={{
        position: 'absolute', borderRadius: '50%',
        border: '1px solid rgba(193,127,89,.3)',
        width: size, height: size,
        animation: `spin ${dur} linear infinite ${reverse ? 'reverse' : ''}`,
        ...pos,
      }} />
    ))}

    <Box component="svg" viewBox="0 0 500 700" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: .18 }}>
      <circle cx="250" cy="350" r="200" stroke="#8B5E3C" strokeWidth=".8" fill="none"/>
      <circle cx="250" cy="350" r="150" stroke="#8B5E3C" strokeWidth=".5" fill="none"/>
      <line x1="50"  y1="350" x2="450" y2="350" stroke="#8B5E3C" strokeWidth=".6"/>
      <line x1="250" y1="150" x2="250" y2="550" stroke="#8B5E3C" strokeWidth=".6"/>
      <line x1="109" y1="209" x2="391" y2="491" stroke="#8B5E3C" strokeWidth=".4"/>
      <line x1="391" y1="209" x2="109" y2="491" stroke="#8B5E3C" strokeWidth=".4"/>
      <ellipse cx="250" cy="350" rx="200" ry="80"  stroke="#8B5E3C" strokeWidth=".5" fill="none"/>
      <ellipse cx="250" cy="350" rx="80"  ry="200" stroke="#8B5E3C" strokeWidth=".5" fill="none"/>
      <circle  cx="250" cy="350" r="12"  stroke="#8B5E3C" strokeWidth="1"  fill="none"/>
      <circle  cx="250" cy="350" r="4"   fill="#8B5E3C" opacity=".4"/>
    </Box>

    <Stack direction="row" alignItems="center" gap={1.25} sx={{ position: 'absolute', top: 44, left: 44 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: T.terracotta }} />
      <Typography sx={{ fontFamily: T.serif, fontSize: 18, fontWeight: 600, color: T.brown, letterSpacing: '.5px' }}>
        {brand}
      </Typography>
    </Stack>

    <Box sx={{ position: 'absolute', bottom: 52, left: 44, right: 44 }}>
      <Typography sx={{ fontFamily: T.serif, fontSize: 22, fontWeight: 500, color: T.brown, lineHeight: 1.55 }}>
        "Design is not just what it looks like. Design is how it works."
      </Typography>
      <Typography sx={{ display: 'block', mt: 1.75, fontSize: 12, letterSpacing: '1.2px', textTransform: 'uppercase', color: `${T.brown}80` }}>
        — Steve Jobs
      </Typography>
    </Box>
  </Box>
);

/* ─────────────────────────────────────────────────────────────────
   ANIMATION HELPER
   "forwards" en vez de "both":
     - "both"    → aplica el keyframe inicial (opacity:0) DURANTE el delay
                   → el elemento es invisible hasta que comienza la animación  ← LCP issue
     - "forwards" → el elemento parte de su estado natural (visible) durante
                    el delay y sólo retiene el estado final al terminar      ← correcto
───────────────────────────────────────────────────────────────── */
const animated = (delay = 0) => ({
  animation: `fadeUp .5s ${delay}s cubic-bezier(.16,1,.3,1) forwards`,
});

/* ─────────────────────────────────────────────────────────────────
   VALIDATION
───────────────────────────────────────────────────────────────── */
const isEmail    = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isPassword = (v) => v.length >= 6;

/* ─────────────────────────────────────────────────────────────────
   I18N
───────────────────────────────────────────────────────────────── */
const TEXTS = {
  en: {
    brand: 'EmojiSurveys', title: 'Welcome', accent: 'back.',
    subtitle: 'Sign in to continue to your account',
    email: 'Email address', emailHelper: 'Please enter a valid email',
    emailPlaceholder: 'your@email.com',
    password: 'Password', passwordHelper: 'Minimum 6 characters',
    passwordPlaceholder: '••••••••',
    forgot: 'Forgot password?',
    formError: 'Please correct the errors before continuing',
    submit: 'Sign In', or: 'or continue with',
    google: 'Google',
    loginError: 'Login error: ', googleError: 'Google sign-in error: ',
    noAccount: "Don't have an account?",
    register: 'Sign up',
    legalPrefix: 'Before using this app, you can review our',
    privacy: 'Privacy Policy',
    and: 'and',
    terms: 'Terms of Service',
    legalSuffix: '.',
    footer: 'Secured with end-to-end encryption',
  },
  es: {
    brand: 'EmojiSurveys', title: 'Bienvenido', accent: 'de nuevo.',
    subtitle: 'Inicia sesión para continuar',
    email: 'Correo electrónico', emailHelper: 'Ingresa un email válido',
    emailPlaceholder: 'tu@email.com',
    password: 'Contraseña', passwordHelper: 'Mínimo 6 caracteres',
    passwordPlaceholder: '••••••••',
    forgot: '¿Olvidaste tu contraseña?',
    formError: 'Corrige los errores antes de continuar',
    submit: 'Iniciar Sesión', or: 'o continúa con',
    google: 'Google',
    loginError: 'Error: ', googleError: 'Error con Google: ',
    noAccount: '¿No tienes cuenta?',
    register: 'Regístrate',
    legalPrefix: 'Antes de usar esta app puedes revisar nuestro',
    privacy: 'Aviso de Privacidad',
    and: 'y los',
    terms: 'Términos de Servicio',
    legalSuffix: '.',
    footer: 'Protegido con cifrado de extremo a extremo',
  },
};

/* ─────────────────────────────────────────────────────────────────
   LEGAL LINK
───────────────────────────────────────────────────────────────── */
const LegalLink = ({ href, children }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      color: T.terracotta,
      textDecoration: 'none',
      fontWeight: 500,
      transition: 'color .2s',
      '&:hover': { color: T.terracottaDark, textDecoration: 'underline' },
    }}
  >
    {children}
  </Box>
);

/* ─────────────────────────────────────────────────────────────────
   LOGIN COMPONENT
───────────────────────────────────────────────────────────────── */
const Login = () => {
  const { loginWithGoogle, loginWithEmail } = useAuth();

  // ── Idioma: derivar lang una sola vez ──────────────────────────────────────
  const isSpanish = navigator.language.startsWith('es');
  const t         = TEXTS[isSpanish ? 'es' : 'en'];
  const lang      = isSpanish ? 'es' : 'en';   // 'es' | 'en'  → sufijo de ruta

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [touched,  setTouched]  = useState({ email: false, password: false });

  const touch = (field) => setTouched((p) => ({ ...p, [field]: true }));

  const emailErr    = touched.email    && email    && !isEmail(email);
  const passwordErr = touched.password && password && !isPassword(password);
  const valid       = email && password && isEmail(email) && isPassword(password);

  // ── Email login ─────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault();
    setTouched({ email: true, password: true });
    if (!valid) { setError(t.formError); return; }

    setLoading(true);
    setError('');
    try {
      await loginWithEmail(email, password);
    } catch (err) {
      setError(t.loginError + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Google login ─────────────────────────────────────────────────────────────
  const handleGoogle = async () => {
    setLoading(true);
    setError('');
    try {
      const credential = await loginWithGoogle();
      await saveUserData(credential.user, 'google');
    } catch (err) {
      setError(t.googleError + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {globalStyles}
      <Box sx={{
        fontFamily: T.sans,
        minHeight: '100vh',
        display: 'flex',
        background: T.cream,
        overflow: 'hidden',
      }}>
        {/* Left panel */}
        <DecorativePanel brand={t.brand} />

        {/* Right form area */}
        <Box sx={{
          flex: 1, display: 'flex', alignItems: 'center',
          justifyContent: 'center', p: '40px 32px', position: 'relative',
          '&::before': {
            content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
            background: `radial-gradient(ellipse 60% 50% at 90% 10%,rgba(193,127,89,.07) 0%,transparent 60%),
                         radial-gradient(ellipse 50% 60% at 10% 90%,rgba(160,190,150,.06) 0%,transparent 60%)`,
          },
        }}>
          <Box sx={{
            position: 'relative', zIndex: 1,
            width: '100%', maxWidth: 400,
            animation: 'cardIn .65s cubic-bezier(.16,1,.3,1) both',
          }}>

            {/* Logo */}
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 4.5, ...animated(.05) }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '10px',
                background: 'linear-gradient(135deg,#c17f59,#d9a07a)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(193,127,89,.3)',
              }}>
                <LayersSVG />
              </Box>
              <Typography sx={{ fontFamily: T.serif, fontSize: 19, fontWeight: 600, color: T.brownDeep, letterSpacing: '.3px' }}>
                {t.brand}
              </Typography>
            </Stack>

            {/* Heading — elemento LCP
                Con "forwards" el h2 es visible de inmediato (estado natural),
                anima suavemente, y retiene el estado final.
                Con "both" (anterior) empezaba en opacity:0 durante el delay. */}
            <Typography component="h2" sx={{
              fontFamily: T.serif, fontSize: 38, fontWeight: 700,
              color: T.brownDeep, lineHeight: 1.15, letterSpacing: '-.5px',
              mb: 1, ...animated(.1),
            }}>
              {t.title} <Box component="em" sx={{ fontStyle: 'italic', color: T.terracotta }}>{t.accent}</Box>
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 300, color: T.muted, mb: 4.5, letterSpacing: '.1px', ...animated(.15) }}>
              {t.subtitle}
            </Typography>

            {/* Error alert */}
            {error && (
              <Alert severity="error" sx={{
                mb: 2.5, borderRadius: '10px', fontFamily: T.sans, fontSize: 13,
                borderLeft: '3px solid #f87171',
                animation: 'shake .35s ease',
              }}>
                {error}
              </Alert>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Box sx={{ mb: 2.25, ...animated(.2) }}>
                <WarmField
                  fullWidth label={t.email} type="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => touch('email')}
                  placeholder={t.emailPlaceholder}
                  error={!!emailErr} helperText={emailErr ? t.emailHelper : ''}
                  disabled={loading} autoComplete="email"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/>
                        </svg>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ mb: 1, ...animated(.25) }}>
                <WarmField
                  fullWidth label={t.password}
                  type={showPw ? 'text' : 'password'}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => touch('password')}
                  placeholder={t.passwordPlaceholder}
                  error={!!passwordErr} helperText={passwordErr ? t.passwordHelper : ''}
                  disabled={loading} autoComplete="current-password"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 8h-1V6c0-2.8-2.2-5-5-5S7 3.2 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.7 1.4-3.1 3.1-3.1 1.7 0 3.1 1.4 3.1 3.1v2z"/>
                        </svg>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" tabIndex={-1} disabled={loading}
                          onClick={() => setShowPw((v) => !v)}
                          sx={{ color: '#c4a48a', '&:hover': { color: T.terracotta } }}
                        >
                          {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Forgot */}
              <Box sx={{ textAlign: 'right', mb: 3, ...animated(.28) }}>
                <Typography component="span" onClick={() => {}} sx={{
                  fontSize: 12, color: T.terracotta, cursor: 'pointer',
                  transition: 'color .2s',
                  '&:hover': { color: T.terracottaDark },
                }}>
                  {t.forgot}
                </Typography>
              </Box>

              {/* Submit */}
              <PrimaryBtn
                type="submit" fullWidth disabled={loading || !valid}
                sx={animated(.3)}
              >
                {loading
                  ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                  : t.submit}
              </PrimaryBtn>
            </Box>

            {/* Divider */}
            <Box sx={{ my: 2.75, ...animated(.35) }}>
              <Divider sx={{
                '&::before,&::after': { borderColor: T.sand },
                '& .MuiDivider-wrapper': {
                  fontFamily: T.sans, fontSize: 11, fontWeight: 400,
                  letterSpacing: '.8px', textTransform: 'uppercase', color: '#c4a88a',
                  px: 1.75,
                },
              }}>
                {t.or}
              </Divider>
            </Box>

            {/* Google */}
            <OutlineBtn
              fullWidth startIcon={<GoogleSVG />}
              onClick={handleGoogle} disabled={loading}
              sx={animated(.4)}
            >
              {t.google}
            </OutlineBtn>

            {/* ── Register redirect ───────────────────────────────────────── */}
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap={0.75}
              sx={{ mt: 3, ...animated(.45) }}
            >
              <Typography sx={{
                fontSize: 13, color: T.muted, fontFamily: T.sans,
              }}>
                {t.noAccount}
              </Typography>
              <Box
                component="a"
                href="/register"
                sx={{
                  fontSize: 13, fontWeight: 500,
                  color: T.terracotta,
                  fontFamily: T.sans,
                  textDecoration: 'none',
                  transition: 'color .2s',
                  '&:hover': { color: T.terracottaDark, textDecoration: 'underline' },
                }}
              >
                {t.register}
              </Box>
            </Stack>

            {/* ── Legal notice — rutas con sufijo de idioma ────────────────── */}
            <Typography sx={{
              textAlign: 'center',
              mt: 2.5,
              fontSize: 11.5,
              color: '#b8a090',
              fontFamily: T.sans,
              lineHeight: 1.6,
              ...animated(.5),
            }}>
              {t.legalPrefix}{' '}
              <LegalLink href={`/aviso-privacidad-${lang}`}>{t.privacy}</LegalLink>
              {' '}{t.and}{' '}
              <LegalLink href={`/terminos-servicio-${lang}`}>{t.terms}</LegalLink>
              {t.legalSuffix}
            </Typography>

          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Login;